import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { LockOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../../config/api.js";
import { ELSA_BRAND_ASSETS } from "../../config/brandAssets";
import "./PasswordReset.css";

const { Title, Text } = Typography;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [serverMessage, setServerMessage] = useState("");
  const [serverError, setServerError] = useState("");
  const [form] = Form.useForm();
  const pending = useRef(null);
  const generation = useRef(0);
  const token = location.state?.resetToken || searchParams.get("token");
  useEffect(() => {
    generation.current += 1;
    pending.current?.abort(); pending.current = null;
    setLoading(false); setServerMessage(""); setServerError(""); form.resetFields();
    return () => { generation.current += 1; pending.current?.abort(); };
  }, [token, form]);

  const onFinish = async ({ password }) => {
    if (pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    const operation = ++generation.current;
    setLoading(true);
    setServerMessage("");
    setServerError("");

    try {
      const response = await axios.post(
        API_CONFIG.apiUrl("/password-reset/reset"),
        { token, password }, { signal: controller.signal }
      );
      if (operation !== generation.current) return;
      form.resetFields();
      setServerMessage(
        response.data?.message ||
          "Votre mot de passe a été réinitialisé avec succès."
      );
    } catch (error) {
      if (operation !== generation.current || error.code === "ERR_CANCELED") return;
      setServerError(
        error.response?.data?.message ||
          "Impossible de réinitialiser le mot de passe."
      );
    } finally {
      if (operation === generation.current) { pending.current = null; setLoading(false); }
    }
  };

  return (
    <div className="password-reset-shell">
      <Card className="password-reset-card">
        <div className="password-reset-brand">
          <img
            src={ELSA_BRAND_ASSETS.logoHorizontalColor}
            alt="ELSA GESTION"
            className="password-reset-brand__logo password-reset-brand__logo--light"
          />
          <img src={ELSA_BRAND_ASSETS.logoHorizontalWhite} alt="ELSA GESTION"
            className="password-reset-brand__logo password-reset-brand__logo--dark" />
          <span className="password-reset-brand__eyebrow">Sécurité du compte</span>
          <Title level={3} className="password-reset-brand__title">
            Nouveau mot de passe
          </Title>
          <Text className="password-reset-brand__subtitle">
            Choisissez un nouveau mot de passe pour votre compte.
          </Text>
        </div>

        {!token && (
          <Alert
            type="error"
            showIcon
            message="Lien de réinitialisation manquant."
            style={{ marginBottom: 20 }}
          />
        )}

        {serverMessage && (
          <Alert
            type="success"
            showIcon
            message={serverMessage}
            style={{ marginBottom: 20 }}
          />
        )}

        {serverError && (
          <Alert
            type="error"
            showIcon
            message={serverError}
            style={{ marginBottom: 20 }}
          />
        )}

        {!serverMessage && token && (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            className="password-reset-form"
          >
            <Form.Item
              name="password"
              label="Nouveau mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir un nouveau mot de passe.",
                },
                {
                  min: 6,
                  message: "Le mot de passe doit contenir au moins 6 caractères.",
                },
                { validator: (_, value) => !value || new Blob([value]).size <= 72 ? Promise.resolve()
                  : Promise.reject(new Error("Le mot de passe ne doit pas dépasser 72 octets (certains caractères comptent pour plusieurs octets).")) },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Nouveau mot de passe"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirmer le mot de passe"
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: "Veuillez confirmer votre mot de passe.",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Les mots de passe ne correspondent pas.")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirmer le mot de passe"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              className="password-reset-button"
            >
              Réinitialiser
            </Button>
          </Form>
        )}

        <div className="password-reset-footer">
          <p className="password-reset-footer__contact">
            <a href="https://www.elsagestion.com">www.elsagestion.com</a>
            <br />
            <a href="tel:+22664989099">+226 64 98 90 99</a>
          </p>
          <Link to="/login" className="password-reset-footer__back">
            Retour à la connexion
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;
